import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { wgs84ToGcj02 } from '@/lib/geo';
import { upsertDriverLocation } from '@/lib/driverLocation';

/**
 * #789 司机位置后台上报（方案 B）
 *
 * 司机角色登录后自动开启 navigator.geolocation.watchPosition，
 * 坐标 WGS-84 → GCJ-02 转换后写入 localStorage `changfa_driver_locations`
 * （白名单键 → 同步 + 备份；HOT_PUSH_DOMAINS 热推，老板端准实时可见）。
 *
 * 写入节流（防打爆同步引擎/服务器）：
 *  - 距上次上报 ≥ 25 秒，且（位移 ≥ 30 米 或 距上次上报 ≥ 5 分钟心跳）；
 *  - 后台节流由浏览器自然限制（visibilitychange 冻结时不回调）。
 *
 * 组件不渲染任何 UI。
 */

const MIN_REPORT_INTERVAL_MS = 25 * 1000;   // 两次上报最小间隔
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 原地不动也定期心跳，保住「在线」状态
const MIN_MOVE_METERS = 30;                // 位移阈值

function distMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export function DriverLocationReporter() {
  const { user } = useAuth();
  const lastReportRef = useRef<{ lat: number; lng: number; at: number } | null>(null);

  const isDriver = !!user && user.role === 'driver';

  useEffect(() => {
    if (!isDriver || !user) return;
    // 司机身份标识：账号 username 为稳定 id；displayName 展示
    const driverId = user.username || user.displayName;
    const driverName = user.displayName || user.username || '司机';
    if (!driverId) return;

    let watchId: number | null = null;
    let heartbeatTimer: number | null = null;

    const report = (latWgs: number, lngWgs: number, accuracy?: number, speedMs?: number) => {
      try {
        const [lng, lat] = wgs84ToGcj02(lngWgs, latWgs);
        const now = Date.now();
        const last = lastReportRef.current;
        if (last) {
          const moved = distMeters(last.lat, last.lng, lat, lng);
          const elapsed = now - last.at;
          if (elapsed < MIN_REPORT_INTERVAL_MS) return;
          if (moved < MIN_MOVE_METERS && elapsed < HEARTBEAT_INTERVAL_MS) return;
        }
        lastReportRef.current = { lat, lng, at: now };
        upsertDriverLocation({
          id: driverId,
          name: driverName,
          lat,
          lng,
          accuracy,
          speed: typeof speedMs === 'number' && speedMs >= 0 ? Math.round(speedMs * 3.6) : undefined,
          updatedAt: now,
        });
      } catch {
        // 忽略单次上报失败（localStorage 满等极端情况），下次再试
      }
    };

    const onPos = (pos: GeolocationPosition) => {
      report(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy ?? undefined, pos.coords.speed ?? undefined);
    };

    if (navigator.geolocation) {
      watchId = navigator.geolocation.watchPosition(onPos, () => {}, {
        enableHighAccuracy: true,
        timeout: 30 * 1000,
        maximumAge: 15 * 1000,
      });
    }

    // 心跳：原地停车时 watchPosition 可能长时间不回调，用定时 getCurrentPosition 保住在线状态
    heartbeatTimer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      navigator.geolocation?.getCurrentPosition(onPos, () => {}, {
        enableHighAccuracy: false,
        timeout: 20 * 1000,
        maximumAge: 60 * 1000,
      });
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      if (heartbeatTimer != null) clearInterval(heartbeatTimer);
    };
  }, [isDriver, user]);

  return null;
}
